// Sticky Add to Cart — appears only after you've scrolled past the native ATC;
// adds via /cart/add.js; opens cart drawer; syncs variant/qty with main form.
class StickyAddToCart {
  constructor() {
    this.stickyAtc = document.getElementById('sticky-atc');
    if (!this.stickyAtc) return;

    // Main product form + native ATC (used as the visibility trigger & data source)
    this.mainForm = this.getMainForm();
    this.mainAtcButton = this.mainForm ? this.mainForm.querySelector('[data-add-to-cart]') : null;

    // Sticky controls
    this.stickyButton = this.stickyAtc.querySelector('.sticky-atc__button');
    this.stickyQty = this.stickyAtc.querySelector('[data-sticky-qty]');
    this.stickyVariantSelect = document.getElementById('sticky-variant-select');

    // Main form fields are the source of truth
    this.mainVariantField =
      (this.mainForm && (this.mainForm.querySelector('input[name="id"]') || this.mainForm.querySelector('select[name="id"]'))) ||
      null;
    this.mainQtyField = this.mainForm ? this.mainForm.querySelector('input[name="quantity"]') : null;

    // internal cache for trigger position (bottom of native ATC)
    this._atcBottom = 0;

    this.init();
  }

  init() {
    this.setupVisibilityTrigger(); // strictly "after passing the native ATC"
    this.setupVariantSync();
    this.setupQtySync();
    this.setupQtyButtons();        // desktop − / +
    this.setupAddToCart();
    this.watchCartDrawer();        // hide sticky while drawer is open
  }

  // Prefer the product form inside the same section as the sticky bar
  getMainForm() {
    const sectionEl = this.stickyAtc.closest('[id^="shopify-section-"]');
    let form = sectionEl
      ? sectionEl.querySelector('form.product-single__form[action*="/cart/add"]')
      : document.querySelector('form.product-single__form[action*="/cart/add"]');
    if (!form) form = (sectionEl || document).querySelector('form[action*="/cart/add"]');
    return form || null;
  }

  /* =========================
     Visibility: show ONLY after the page has scrolled PAST the native ATC.
     Rule: window.scrollY > (ATC.offsetTop + ATC.offsetHeight)
     Includes resize/orientation + a light IO fallback.
  ========================= */
  setupVisibilityTrigger() {
    if (!this.mainAtcButton) return;

    const recompute = () => {
      const rect = this.mainAtcButton.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      this._atcBottom = rect.top + scrollTop + rect.height;
      this.evaluateVisibility();
    };

    this.evaluateVisibility = () => {
      // If cart drawer is open, never show
      if (document.body.classList.contains('cart-drawer-open')) {
        this.hideSticky();
        return;
      }
      const scrolledPast = (window.pageYOffset || document.documentElement.scrollTop || 0) > this._atcBottom;
      if (scrolledPast) this.showSticky(); else this.hideSticky();
    };

    // Initial compute + listeners
    recompute();
    window.addEventListener('scroll', this.evaluateVisibility, { passive: true });
    window.addEventListener('resize', recompute);

    // IntersectionObserver as a gentle helper (handles dynamic content shifts)
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some(e => e.intersectionRatio === 0)) {
          recompute();
        }
      },
      { root: null, threshold: [0, 1] }
    );
    io.observe(this.mainAtcButton);
  }

  showSticky() {
    if (this.stickyAtc.style.display !== 'block') this.stickyAtc.style.display = 'block';
    this.stickyAtc.classList.add('show');
  }

  hideSticky() {
    this.stickyAtc.classList.remove('show');
    // small delay to allow CSS transition to finish before removing from flow
    window.clearTimeout(this._hideT);
    this._hideT = window.setTimeout(() => {
      if (!this.stickyAtc.classList.contains('show')) this.stickyAtc.style.display = 'none';
    }, 200);
  }

  // Keep sticky synced with variant/availability and update prices
  setupVariantSync() {
    if (this.stickyVariantSelect && this.mainVariantField) {
      this.stickyVariantSelect.addEventListener('change', (e) => {
        const variantId = e.target.value;
        this.setMainVariant(variantId, true);
      });
    }

    document.addEventListener('variant:change', (e) => {
      const v = e && e.detail && e.detail.variant;
      if (!v) return;

      v.available ? this.enableSticky() : this.disableSticky('soldOut');
      if (this.stickyButton) this.stickyButton.dataset.variantId = String(v.id);

      const priceNodes = this.stickyAtc.querySelectorAll('[data-sticky-price]');
      priceNodes.forEach((n) => {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          n.textContent = Shopify.formatMoney(v.price);
        }
      });

      const compareNodes = this.stickyAtc.querySelectorAll('[data-sticky-compare]');
      compareNodes.forEach((n) => {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          if (window.Shopify && typeof Shopify.formatMoney === 'function') {
            n.textContent = Shopify.formatMoney(v.compare_at_price);
          }
          n.style.display = '';
        } else {
          n.style.display = 'none';
        }
      });
    });
  }

  // Qty mirrors between sticky and main form (clamped)
  setupQtySync() {
    const clamp = (v) => {
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    };

    if (this.stickyQty) {
      this.stickyQty.setAttribute('step', '1');
      this.stickyQty.setAttribute('min', '1');

      this.stickyQty.addEventListener('change', () => {
        const q = clamp(this.stickyQty.value);
        this.stickyQty.value = q; // reflect clamp
        if (this.mainQtyField) this.mainQtyField.value = q;
      });
    }

    if (this.mainQtyField && this.stickyQty) {
      this.mainQtyField.addEventListener('change', () => {
        const q = clamp(this.mainQtyField.value);
        this.stickyQty.value = q;
      });
    }
  }

  // Desktop − / + (direct listeners only; prevent bubbling + default)
  setupQtyButtons() {
    const wrap = this.stickyAtc.querySelector('.js-sticky-qty');
    if (!wrap || !this.stickyQty) return;

    const minusBtn = wrap.querySelector('[data-minus]');
    const plusBtn  = wrap.querySelector('[data-plus]');

    const clamp = (v) => {
      const n = parseInt(v, 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    };
    const set = (next) => {
      this.stickyQty.value = clamp(next);
      this.stickyQty.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const get = () => clamp(this.stickyQty.value);

    minusBtn?.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      set(get() - 1);
    });
    plusBtn?.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      set(get() + 1);
    });

    // Keyboard arrows on the input
    this.stickyQty?.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp')   { e.preventDefault(); set(get() + 1); }
      if (e.key === 'ArrowDown') { e.preventDefault(); set(get() - 1); }
    });
  }

  setupAddToCart() {
    if (!this.stickyButton) return;

    this.stickyButton.addEventListener('click', async (e) => {
      e.preventDefault();
      if (this.stickyButton.hasAttribute('disabled')) return;

      const variantId = this.getCurrentVariantId();
      if (!variantId) return;

      const qty = this.getCurrentQty();

      const btnTextEl = this.stickyButton.querySelector('.btn__text');
      const prevText = btnTextEl ? btnTextEl.textContent : '';
      this.stickyButton.classList.add('is-loading');
      this.stickyButton.setAttribute('disabled', 'disabled');
      if (btnTextEl) btnTextEl.textContent = '…';

      try {
        await this.addToCartViaAjax(variantId, qty);
        this.dispatchCartAdded({ id: variantId, quantity: qty });
        this.openCartDrawer();

        if (btnTextEl) {
          btnTextEl.textContent = '✓';
          setTimeout(() => (btnTextEl.textContent = prevText), 900);
        }
      } catch (err) {
        console.error('Sticky ATC add error:', err);
        if (btnTextEl) {
          btnTextEl.textContent = 'Error';
          setTimeout(() => (btnTextEl.textContent = prevText), 1400);
        }
      } finally {
        this.stickyButton.classList.remove('is-loading');
        this.stickyButton.removeAttribute('disabled');
      }
    });
  }

  // Hide sticky while drawer is open (prevents overlap with drawer)
  watchCartDrawer() {
    const drawer = document.getElementById('CartDrawer');
    const body = document.body;

    const setOpen = (open) => {
      body.classList.toggle('cart-drawer-open', open);
      if (open) {
        this.hideSticky();
      } else {
        this.evaluateVisibility && this.evaluateVisibility();
      }
    };

    document.addEventListener('cart:open', () => setOpen(true));
    document.addEventListener('cart:close', () => setOpen(false));

    if (drawer) {
      const mo = new MutationObserver(() => {
        setOpen(drawer.classList.contains('is-open'));
      });
      mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.js-drawer-close');
      if (closeBtn) setOpen(false);
    });
  }

  // Helpers
  getCurrentVariantId() {
    const val =
      (this.mainVariantField && (this.mainVariantField.value || this.mainVariantField.getAttribute('value'))) ||
      (this.stickyVariantSelect && this.stickyVariantSelect.value) ||
      (this.stickyButton && this.stickyButton.dataset.variantId);
    const id = parseInt(val, 10);
    return Number.isFinite(id) ? id : null;
  }

  getCurrentQty() {
    const q = (this.stickyQty && this.stickyQty.value) ?? (this.mainQtyField && this.mainQtyField.value) ?? 1;
    const num = parseInt(q, 10);
    return Number.isFinite(num) && num > 0 ? num : 1;
  }

  clampQty(v) {
    v = parseInt(v || 1, 10);
    if (isNaN(v) || v < 1) v = 1;
    return v;
  }

  setMainVariant(variantId, bubbleChange) {
    if (!this.mainVariantField) return;
    this.mainVariantField.value = String(variantId);
    if (bubbleChange) this.mainVariantField.dispatchEvent(new Event('change', { bubbles: true }));
  }

  enableSticky() {
    this.stickyButton?.removeAttribute('disabled');
    const t = this.stickyButton?.querySelector('.btn__text');
    if (t && window.theme?.strings?.addToCart) t.textContent = theme.strings.addToCart;
  }

  disableSticky(reason) {
    this.stickyButton?.setAttribute('disabled', 'disabled');
    const t = this.stickyButton?.querySelector('.btn__text');
    if (t && window.theme?.strings) {
      t.textContent =
        reason === 'soldOut' ? (theme.strings.soldOut || 'Sold out') : (theme.strings.unavailable || 'Unavailable');
    }
  }

  async addToCartViaAjax(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Add to cart failed: ${res.status} ${text}`);
    }
    return res.json();
  }

  dispatchCartAdded(detail) {
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail }));
    document.dispatchEvent(new CustomEvent('cart:updated-request'));
    this.updateCartCountSilently();
  }

  async updateCartCountSilently() {
    try {
      const cart = await (await fetch('/cart.js')).json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'block' : 'none';
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (_) {}
  }

  openCartDrawer() {
    document.dispatchEvent(new CustomEvent('cart:open'));
    const toggle = document.querySelector('[data-cart-drawer-toggle], .js-cart-drawer-toggle, [data-drawer-toggle="cart"]');
    if (toggle && typeof toggle.click === 'function') {
      toggle.click();
      return;
    }
    if (window.theme && window.theme.cart && typeof window.theme.cart.open === 'function') {
      window.theme.cart.open();
    }
  }
}

// Boot
document.addEventListener('DOMContentLoaded', () => {
  new StickyAddToCart();
});